import { connect } from 'react-redux';
import React from 'react';
import { waitForInvData } from 'wsm-srp-utilities';
import { ErrorBoundary } from 'wsm-error-boundary';
import {
	addNewRelicPageAction,
	setNewRelicCustomAttribute
} from 'ddc-new-relic';
import PropTypes from 'prop-types';
import { Common } from 'ws-scripts/modules/common';
import { isBrowser } from 'ws-scripts/modules/environment';
import { insertWIAPIImagesByInventory } from '../utilities/insertWIAPIImages';
import NoResults from '../components/NoResults';
import VehicleCards from '../components/VehicleCards';
import LayoutToggle from '../components/LayoutToggle';
import ComparePortal from '../components/ComparePortal';
import ErrorMessage from '../components/ErrorMessage';
import MediaModalContainer from '../components/MediaModalContainer';

export class WidgetContainer extends React.PureComponent {
	constructor(props) {
		super(props);

		if (isBrowser) {
			waitForInvData(this);
		}

		this.state = {
			isLoading: true,
			isRequesting: false,
			isTransition: false,
			inventory: {
				accounts: {},
				incentives: {},
				pageInfo: {
					enableMediaCarousel: false,
					enableMyCars: false,
					enableMyCarsOnVLP: false
				},
				inventory: [...Array(6)].map((_, i) => ({ uuid: `${i}` }))
			},
			appliedFilters: [],
			requestFailed: false,
			// if the imageDelayLoad pref is set, set the loading delay to true
			loadingDelayed: +this.props.prefs.imageDelayLoad > 0
		};
	}

	componentDidMount() {
		// If imageDelayLoad preference is set, start the delay timer
		if (+this.props.prefs.imageDelayLoad > 0) {
			this.delayTimer = setTimeout(() => {
				// toggle delay loading off when the timer expires
				this.setState(prevState => ({
					...prevState,
					loadingDelayed: false
				}));
			}, +this.props.prefs.imageDelayLoad);
		}

		// Listen for event to update with new images
		if (isBrowser) {
			window.addEventListener(
				'wiapiAllVehiclesMediaUpdated',
				this.handleImagesInsertEventlistener.bind(this)
			);
		}

		// Send info to NR about default layout
		setNewRelicCustomAttribute('srpLayout', this.props.layout.layout);

		if (
			isBrowser &&
			window.DDC &&
			window.DDC.dataLayer &&
			window.DDC.dataLayer.page &&
			window.DDC.dataLayer.page.attributes
		) {
			window.DDC.dataLayer.page.attributes.srpLayout = this.props.layout.layout;
		}

		// Pull localstorage layout data, if available
		if (this.props.layout.allowLayoutToggle) {
			const layout = localStorage.getItem('srpLayout');
			if (layout !== null && this.props.layout.layout !== layout) {
				this.props.setLayout({
					layout
				});
			}
		}
		const pubsub =
			(isBrowser && window.DDC && window.DDC.pubsub) || undefined;

		if (pubsub) {
			// Subscribing to fetch request events in ws-inv-data
			window.DDC.pubsub.subscribe('ws-inv-data/inventory-request', () => {
				if (!this.state.isLoading) {
					this.setState({ isRequesting: true });
				}
			});

			window.DDC.pubsub.subscribe('ws-inv-data/inventory', () => {
				this.initializeListings();
			});
			// There is a chance that the topic 'ws-inv-data/inventory' is published by inv-data
			// before the inv-listing subscribes to it. This results in the listings being stuck
			// in the skeleton loader phase. This issue does not happen when faceting.

			// We fire the initializeListings function under these circumstances:
			// When the component mounts (ie: does not trigger on facet interactions),
			// AND when the state variable 'isLoading' is still true
			// AND when the DDC. InvData.inventory object is not empty (data has already been fetched)
			if (
				this.state.isLoading &&
				window.DDC &&
				window.DDC.InvData &&
				Object.keys(window.DDC.InvData.inventory).length
			) {
				this.initializeListings();
			}

			window.DDC.pubsub.subscribe('ws-inv-data/facets', () => {
				this.setState({
					appliedFilters: window.DDC.InvData.getAppliedFilters()
				});
			});
			window.DDC.pubsub.subscribe('ws-inv-data/request-failed', () => {
				this.setState({
					requestFailed: true,
					isLoading: false
				});
				this.removeWidgetHeight();
			});
			window.DDC.pubsub.subscribe('instant-ePrice-completed', () => {
				let profile = window.DDC.userProfile;
				if (
					!(
						typeof profile === 'object' &&
						Object.keys(profile).length > 0
					)
				) {
					profile = null;
				}
				this.props.setUserProfile({
					userProfile: profile
				});
			});
			// Transition display for mobile on ws-inv-filters events
			if (this.props.deviceType === 'MOBILE') {
				window.DDC.pubsub.subscribe('ws-inv-filters/modal-show', () => {
					this.setState({ isTransition: true });
				});
				window.DDC.pubsub.subscribe('ws-inv-filters/modal-hide', () => {
					this.setState({ isTransition: false });
				});
			}
		}
	}

	componentDidUpdate() {
		const pubsub =
			(isBrowser && window.DDC && window.DDC.pubsub) || undefined;

		if (pubsub) {
			window.DDC.pubsub.publish('ws-inv-listing-update');
		}

		// Set a flag so the Web Integration API knows the initial inventory view has rendered.
		window.DDC.PrivateAPI = window.DDC.PrivateAPI || {};
		window.DDC.PrivateAPI.wsInvListingRendered = true;

		// Required refresh to allow `.dialog` elements to initialize jQuery
		// dialog and slide-in functionality on wsm-vehicle-cta-display buttons
		if (this.props.deviceType === 'MOBILE') {
			window.DDC.mobile.slidein.init();
		} else if (typeof $ === 'function') {
			$(document).trigger('modulesRefresh', {
				modules: {
					dialog: window.DDC.modules.dialog
				}
			});
		}
	}

	componentWillUnmount() {
		// Clean up listener on unmount
		if (isBrowser) {
			window.removeEventListener(
				'wiapiAllVehiclesMediaUpdated',
				this.handleImagesInsertEventlistener.bind(this)
			);
		}
		// if imageDelayLoad preference is set, clear the delayTimer
		if (+this.props.prefs.imageDelayLoad > 0) {
			clearTimeout(this.delayTimer);
		}
	}

	// handler for inserting new images on wiapi event
	handleImagesInsertEventlistener(data) {
		// get the inventory
		const {
			inventory: globalInventory
		} = window.DDC.InvData.getInventory();

		// build the updated inventory with inserted 3rd party content
		const updatedInventory = insertWIAPIImagesByInventory(
			globalInventory,
			data.detail.content
		);
		// if imageDelayLoad preference is set, clear the delayTimer
		if (+this.props.prefs.imageDelayLoad > 0) {
			clearTimeout(this.delayTimer);
		}

		// update state with the new inventory and toggle the loading delay off
		this.setState(prevState => {
			return {
				inventory: {
					...prevState.inventory,
					inventory: updatedInventory
				},
				loadingDelayed: false
			};
		});
	}

	initializeListings() {
		// get the global inventory meta data
		const globalData = window.DDC.InvData.getInventory();

		// get the new gallery content to be inserted
		const galleryContent = window?.DDC?.PrivateAPI?.getAllGalleryContent
			? window.DDC.PrivateAPI.getAllGalleryContent('vehicle-media')
			: {};

		// build the updated inventory with inserted 3rd party content
		const updatedInventory = insertWIAPIImagesByInventory(
			globalData.inventory,
			galleryContent
		);

		// build initialized state
		this.setState({
			isRequesting: false,
			isLoading: false,
			inventory: {
				...globalData,
				inventory: updatedInventory
			}
		});
		this.removeWidgetHeight();
	}

	removeWidgetHeight() {
		const widgetAppId = `${this.props.windowId}-app-root`;
		const widget = document.getElementById(widgetAppId);

		widget.classList.remove('min-vh-100');
	}

	render() {
		if (!isBrowser || !window.DDC.InvData) {
			return null;
		}

		const {
			inventory,
			isLoading,
			requestFailed,
			appliedFilters,
			isTransition,
			isRequesting,
			loadingDelayed
		} = this.state;

		return (
			<ErrorBoundary newRelicPageAction="WS INV LISTING ERROR BOUNDARY">
				<Common
					render={({ prefs, labels }) => {
						const {
							showVehicleCardLayoutToggle,
							showVideoModalButton
						} = prefs;

						const { pageInfo } = inventory;
						const {
							enableMediaCarousel,
							enableMyCarsOnVLP,
							enableMyCars
						} = pageInfo;
						const isMyCarsSitePropEnabled =
							enableMyCars && enableMyCarsOnVLP;

						if (requestFailed) {
							addNewRelicPageAction(
								'WS INV LISTING REQUEST FAILED',
								{
									requestFailed: true
								}
							);
						}
						return requestFailed ? (
							<ErrorMessage
								errorLabel={labels.get(
									'TECHNICAL_DIFFICULTIES_VERBIAGE'
								)}
							/>
						) : (
							<div
								role="region"
								aria-live="polite"
								aria-busy={isLoading}
								aria-label={labels.get('MATCHING_VEHICLES')}
								// TODO add data-async-widget="true" to widget container renderer in ws-scripts
								data-async-widget
							>
								{showVehicleCardLayoutToggle === 'true' && (
									<LayoutToggle portal />
								)}
								<ComparePortal />
								{inventory.pageInfo &&
								inventory.pageInfo.totalCount === 0 ? (
									<NoResults {...{ appliedFilters }} />
								) : (
									<VehicleCards
										{...{
											inventoryObj: inventory,
											isMyCarsSitePropEnabled,
											enableMediaCarousel,
											isLoading,
											isTransition,
											isRequesting,
											loadingDelayed
										}}
									/>
								)}

								{showVideoModalButton === 'true' && (
									<MediaModalContainer />
								)}
							</div>
						);
					}}
				/>
			</ErrorBoundary>
		);
	}
}

WidgetContainer.propTypes = {
	deviceType: PropTypes.string.isRequired,
	windowId: PropTypes.string.isRequired,
	widgetName: PropTypes.string.isRequired,
	pageAlias: PropTypes.string.isRequired,
	layout: PropTypes.shape({
		layout: PropTypes.string.isRequired,
		allowLayoutToggle: PropTypes.bool
	}).isRequired,
	setLayout: PropTypes.func.isRequired,
	setUserProfile: PropTypes.func.isRequired,
	prefs: PropTypes.shape({
		imageDelayLoad: PropTypes.string
	})
};

WidgetContainer.defaultProps = {
	prefs: {
		imageDelayLoad: 0
	}
};

const mapStateToProps = state => {
	const {
		requestData: { deviceType, widgetName, windowId },
		widgetInfo: { pageAlias },
		layout,
		prefs
	} = state;

	return {
		deviceType,
		widgetName,
		windowId,
		pageAlias,
		layout,
		prefs
	};
};

const mapDispatchToProps = dispatch => ({
	setLayout: data => dispatch({ type: 'SET_LAYOUT', payload: data }),
	setUserProfile: data =>
		dispatch({ type: 'SET_USER_PROFILE', payload: data })
});

const Widget = connect(mapStateToProps, mapDispatchToProps)(WidgetContainer);

export default Widget;
